import { html } from 'lit-html';
import { Lit, Component, Prop, BaseComponent, Expose } from '../base';
import { JHADesignerEngine, FocusMode } from '../../engine';
import { MeshType, Side, MeshSubType } from '../../types';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { TextureType } from '../../engine/textures/textureSet';
import { SIDES, TEXTURE_TYPES, WHITE } from '../../engine/constants';
import { parseColor, meshKey } from '../../engine/utilities';
import { LayerOrder } from '../../engine/textures/layerdTexture';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';

export interface IJHADesignerPreview {
  beginTextureChange(meshType: MeshType, sides: Side[], tasks: () => Promise<void>): Promise<void>;
  setColorTex(model: MeshType, side: Side, uri: string, layer: LayerOrder, baseColor?: string, maskUri?: string): Promise<void>
  clearColorTexLayer(model: MeshType, side: Side, layer: LayerOrder): Promise<void>
  setNormalTex(model: MeshType, side: Side, uri: string, layer: LayerOrder, maskUri?: string): Promise<void>
  clearNormalTexLayer(model: MeshType, side: Side, layer: LayerOrder): Promise<void>
  setMetallicRoughnessTex(model: MeshType, side: Side, uri: string, layer: LayerOrder, maskUri?: string): Promise<void>
  clearMetallicRoughessTexLayer(model: MeshType, side: Side, layer: LayerOrder): Promise<void>
  setEmissiveTex(model: MeshType, side: Side, uri: string, layer: LayerOrder, maskUri?: string): Promise<void>
  clearEmissiveTexLayer(model: MeshType, side: Side, layer: LayerOrder): Promise<void>
  setLightIntensity(value: number): Promise<void>;
  onReady(): Promise<void>
  textureFromCanvas(name: string, options: any): Promise<DynamicTexture | void>
  enableControls(enable: boolean, side?: Side): Promise<void>;
  getCanvasElement(): Promise<HTMLCanvasElement>;
  queueScreenshot(width: number, height: number): Promise<string>;
  drawLayers(meshType: MeshType, sides: Side[]): Promise<void>
  setFocus(focus?: FocusMode): void;
}

type CanvasTextures = { [name:string]: DynamicTexture }

@Lit()
@Component('jha-designer-preview', true)
export class JHADesigner extends BaseComponent implements IJHADesignerPreview {
  @Prop() itemCode!: string;
  @Prop() leftIEM: string = "";
  @Prop() rightIEM: string = "";
  @Prop() focus: FocusMode = "center";
  @Prop() scenerooturl?: string;
  @Prop() scenefile?: string;

  private _canvasElement!: HTMLCanvasElement;

  private engine!: JHADesignerEngine;
  private onReadyResolve!: () => void;
  private _onReady: Promise<void>;
  private _canvasTextures: CanvasTextures = {};

  constructor() {
    super();
    this._onReady = new Promise((resolve) => {
      this.onReadyResolve = () => resolve();
    })
  }

  @Expose()
  public async onReady() {
    return this._onReady;
  }

  @Expose()
  public async setLightIntensity(value:number) {
    if ( !this.engine ) {
      await this.onRender();
    }

    if ( this.engine ) {
      this.engine.lightIntensity = value;
    }
  }

  @Expose()
  async textureFromCanvas(name: string, options: any) {
    await this._onReady;

    if ( this.engine ) {
      if ( name.search(/canvas:\/\//i) < 0 ) {
        name = `canvas://${name}`;
      }

      this._canvasTextures[name] = new DynamicTexture(name, options, this.engine.scene, false);

      return this._canvasTextures[name];
    } else {
      throw new Error("Unreachable code reached...")
    }
    
  }

  private async _loadTexture(texType: TextureType, model: MeshType, side: Side, uri: string, layer: LayerOrder, maskUri?: string, baseColor: Color4 = WHITE) {
    await this._onReady;

    // if (!uri) {
    //   await this._clearTexture(texType, model, side, layer);
    //   return;
    // }

    if (this.engine) {
      const tset = this.engine.getTextureSet(model, side)
      if ( uri ) {
        const work: Promise<Texture | undefined>[] = [];

        const applyTextures = (colorTexture:Texture, maskTexture?: Texture) => {
          tset.getTexture(texType).addLayer(layer, texType, colorTexture, false, baseColor, maskTexture);
        }

        if ( uri.search(/canvas:\/\//) == 0 ) {
          let canvasTexture = this._canvasTextures[uri];
          canvasTexture.update();
          work.push(Promise.resolve(canvasTexture));
        } else {
          work.push(this.engine.textureBulkLoader.load(`${model}.${side}.${texType}`, uri).then(task => task.texture));
        }

        if ( maskUri ) {
          if ( maskUri.search(/canvas:\/\//) == 0 ) {
            let maskTexture = this._canvasTextures[maskUri];
            maskTexture.update();
            work.push(Promise.resolve(maskTexture));
          } else {
            work.push(this.engine.textureBulkLoader.load(`${model}.${side}.${texType}.mask`, maskUri).then(task => task.texture));
          }
        } else {
          work.push(Promise.resolve(undefined));
        }

        Promise.all(work).then((texture) => {
          if ( texture[0] ) {
            applyTextures(texture[0], texture[1]);
          }
        }).catch((err) => {
          console.log("Could not load all tasks? ", err);
        })
      } else {
        tset.getTexture(texType).removeLayer(layer);
      }
    }
  }

  private async _clearTexture(texType: TextureType, model: MeshType, side: Side, layer: LayerOrder) {
    if ( !this.engine ) {
      await this.onRender();
    }

    if (this.engine) {
      const tset = this.engine.getTextureSet(model, side);
      tset.getTexture(texType).removeLayer(layer);
      //await tset.getTexture(texType).draw();
    }
  }

  /**
   * Prepares the internal engine asset manager before a large texture change happens
   */
  @Expose()
  async beginTextureChange(meshType: MeshType, sides: Side[], tasks: () => Promise<void>) {
    await this._onReady;

    if (this.engine) {
      try {
        this.engine.textureBulkLoader.prepare();
        await tasks();
        await this.engine.textureBulkLoader.commit();
        await this.drawLayers(meshType, sides);
      }
      catch (err) {
        console.dir(err);
        console.error(err);
      }
    }
  }

  @Expose() 
  async queueScreenshot(width: number, height: number) {
    await this._onReady;

    return await this.engine.queueScreenshot(width, height);
  }

  @Expose()
  async getCanvasElement() {
    await this._onReady;
    return this._canvasElement;
  }

  @Expose()
  async enableControls(enable: boolean = true, side?: Side) {
    await this._onReady;

    if ( this.engine ) {
      await this.engine.enableControls(enable, Math.PI / 2, side);
    }
  }

  @Expose()
  async drawLayers(meshType: MeshType, sides: Side[]) {
    await this._onReady;

    if (this.engine) {
      if ( sides.length == 0 ) {
        sides = SIDES;
      }

      for(let side of sides) {
        const subType: MeshSubType = meshType == 'iem'?'innerouter':'';
        const key = meshKey(meshType, subType, side);
        const textureSet = this.engine.textureSets[key];
        for(let textureType of TEXTURE_TYPES) {
          try {
            const textureInstance = await textureSet.getTexture(textureType)
            textureInstance.draw();
          } catch (err) {
            console.log("Error while drawing texture: ");
            console.error(err);
          }
        }
      }
    }
  }

  @Expose()
  async setColorTex(model: MeshType, side: Side, uri: string, layer: LayerOrder = "Base", baseColor?: string, maskUri?: string) {
    let color = WHITE;
    try {
      color = baseColor?parseColor(baseColor):WHITE;
    } catch(err) {
      console.error("Error while converting color: ", baseColor);
    }
    await this._loadTexture("albedo", model, side, uri, layer, maskUri, color);
  }

  /**
   * Clears one or all layers from the albedo texture on the provided model and side
   * @param model The model to modify its texture set.
   * @param side The side to modify.
   * @param layer The layer to clear. If set to -1 all layers will be cleared.
   */
  @Expose()
  async clearColorTexLayer(model: MeshType, side: Side, layer: LayerOrder = "Base") {
    await this._clearTexture("albedo", model, side, layer);
  }

  @Expose()
  async setNormalTex(model: MeshType, side: Side, uri: string, layer: LayerOrder = "Base", maskUri?: string) {
    await this._loadTexture("normal", model, side, uri, layer, maskUri);
  }

  @Expose()
  async clearNormalTexLayer(model: MeshType, side: Side, layer: LayerOrder = "Base") {
    await this._clearTexture("normal", model, side, layer);
  }

  @Expose()
  async setMetallicRoughnessTex(model: MeshType, side: Side, uri: string, layer: LayerOrder = "Base", maskUri?: string) {
    await this._loadTexture("metalRough", model, side, uri, layer, maskUri);
  }

  @Expose()
  async clearMetallicRoughessTexLayer(model: MeshType, side: Side, layer: LayerOrder = "Base") {
    await this._clearTexture("metalRough", model, side, layer);
  }

  @Expose()
  async setEmissiveTex(model: MeshType, side: Side, uri: string, layer: LayerOrder = "Base", maskUri?: string) {
    await this._loadTexture("emissive", model, side, uri, layer, maskUri, new Color4(0,0,0,1));
  }

  @Expose()
  async clearEmissiveTexLayer(model: MeshType, side: Side, layer: LayerOrder = "Base") {
    await this._clearTexture("emissive", model, side, layer);
  }

  async onRender() {
    const me = this;

    let canvas: HTMLCanvasElement = me._canvasElement = me.root.getElementById("preview") as HTMLCanvasElement;
    if ( canvas ) {
      if ( !me.engine ) {
        me.engine = new JHADesignerEngine(canvas, () => {
          const parent = (me.root as ShadowRoot).host;
          // temporarily shrink canvas so we may measure parent's natural dimensions.
          canvas.width = 0;
          canvas.height = 0;
          const dim = {
            width: parent.clientWidth,
            height: parent.clientHeight
          }
          canvas.width = dim.width;
          canvas.height = dim.height;
          return dim;
        }, me.focus);
        await me.engine.bootstrap({
          ...( me.scenerooturl && { sceneRootUrl: this.scenerooturl }),
          ...( me.scenefile && { sceneFile: this.scenefile })
        });
        me.onReadyResolve();
      }
    } else {
      console.error("Could not find designer canvas!");
    }
  }

  /**
   * Changes the camera offset focus side
   * @param focus Focus mode "left", "center", "right"
   */
  @Expose()
  async setFocus(focus?: FocusMode) {
    await this._onReady;

    this.engine.setFocus(focus);
  }

  render() {
    return html`
      <style>
        :host(jha-designer-preview) {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        #preview {
          display: flex;
          flex: 1 1 auto;
          padding: 0;
          margin: 0;
        }
        #debug {
          display: none;
        }
      </style>
      <canvas id="preview" width="100%" height="100%"></canvas>
    `
  }
}